import { paletteForMode } from "./brandColor";

// Applies a community's chosen font (from Other Settings) to the whole UI.
// Safe to call repeatedly: each Google Font is injected at most once, and the
// active family is mirrored onto <body> plus a --community-font CSS variable so
// Tailwind/inline styles can opt in via var(--community-font).
const loaded = new Set();

export default function applyCommunityFont(font) {
  const family = String(font || "").trim();
  if (!family) return;

  const id = `community-font-${family.replace(/\s+/g, "-").toLowerCase()}`;
  if (!loaded.has(family) && !document.getElementById(id)) {
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
      family
    )}:wght@300;400;500;600;700&display=swap`;
    document.head.appendChild(link);
    loaded.add(family);
  }

  const stack = `'${family}', system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
  document.documentElement.style.setProperty("--community-font", stack);
  document.body.style.fontFamily = stack;
}

// Applies the community's text direction (RTL when enabled) to the whole
// front-end membership. Backend/admin is unaffected — this only runs in the FE.
export function applyCommunityDirection(rtl) {
  const dir = Number(rtl) === 1 ? "rtl" : "ltr";
  document.documentElement.setAttribute("dir", dir);
  document.body.setAttribute("dir", dir);
}

// Publishes the community's brand palette (funnel_membership_settings.brand_settings)
// as global CSS custom properties so any component can honour them without prop
// drilling:
//   --brand-primary    → theme_color1 (also threaded as the `primary` prop)
//   --brand-secondary  → theme_color2 (used as the far stop of primary gradients)
//   --brand-btn-text   → theme_button_txt_color (text on solid brand buttons)
// Every consumer uses a var() fallback, so unset values simply keep the prior look.
//
// The values published are the palette FOR THE CURRENT MODE, not the raw stored
// ones: a brand colour picked against a light canvas can be invisible on the
// dark surface, so paletteForMode rescues the ones that fail (see brandColor.js
// — it is a no-op for a palette that already reads). The creator's literal
// choice is still published as --brand-primary-raw for the rare place that
// wants it regardless, e.g. matching a logo.
//
// `mode` defaults to whatever is painted on <html>, so the many call sites that
// pass only a brand stay correct.
let lastBrand = null;

export function applyCommunityTheme(brand, mode) {
  const b = brand || {};
  lastBrand = b;

  const m = mode || document.documentElement.getAttribute("data-theme") || "light";
  const palette = paletteForMode(b, m);
  const root = document.documentElement.style;

  if (b.theme_color1) root.setProperty("--brand-primary-raw", b.theme_color1);
  if (palette.primary) root.setProperty("--brand-primary", palette.primary);
  if (palette.secondary) root.setProperty("--brand-secondary", palette.secondary);
  root.setProperty("--brand-btn-text", palette.btnText);
}

/**
 * Re-publish the palette for the mode now on <html>.
 *
 * Switching light/dark only flips an attribute, which the neutral ramps pick up
 * on their own — but the brand vars are computed values, so they have to be
 * recomputed. theme.js calls this after every paint, which is why no component
 * has to know that the palette is mode-dependent.
 */
export function refreshCommunityTheme() {
  if (lastBrand) applyCommunityTheme(lastBrand);
}
